import React, { ReactNode, useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import type { DotStatus, OrbitStatusMeta } from '../../../../types/mission';
import type { SensorData } from '../../../../types/sensor';
import CustomToolTip from '../../Common/CustomToolTip';
import styles from '../../LeftSideBar.module.scss';

const EyeOnIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path d="M1 6.5C2.5 3.5 5 2 6.5 2S10.5 3.5 12 6.5C10.5 9.5 8 11 6.5 11S2.5 9.5 1 6.5z" stroke="currentColor" strokeWidth="1.1" />
    <circle cx="6.5" cy="6.5" r="1.8" stroke="currentColor" strokeWidth="1.1" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path d="M1 1l11 11M5 2.5C5.6 2.2 6.1 2 6.5 2 8 2 10.5 3.5 12 6.5c-.5 1-1.2 1.9-2 2.5M3.5 4C2.4 4.8 1.5 5.7 1 6.5 2.5 9.5 5 11 6.5 11c1 0 2-.4 2.9-1" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
  </svg>
);

const DuplicateIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
  </svg>
);

const MoreIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <circle cx="6.5" cy="6.5" r="0.9" fill="currentColor" />
    <circle cx="6.5" cy="3.2" r="0.9" fill="currentColor" />
    <circle cx="6.5" cy="9.8" r="0.9" fill="currentColor" />
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
  </svg>
);

const RenameIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 6.185a6 6 0 0 0-6 0v11.63a6 6 0 0 0 6 0" />
    <path d="M7 6.185a6 6 0 0 1 6 0v11.63a6 6 0 0 1-6 0" />
    <path d="M12 4v16" />
  </svg>
);


const SensorCaretIcon = ({ open }: { open: boolean }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}
    aria-hidden="true"
  >
    <rect width="12" height="12" fill="black" fillOpacity="0.01" />
    <path d="M6.4 7.35c-.22.22-.58.22-.8 0L3.2 4.95c-.36-.36-.1-.98.4-.98h4.8c.5 0 .76.62.4.98L6.4 7.35Z" fill="currentColor" />
  </svg>
);

interface TreeItemProps {
  id: string;
  name: string;
  dot: DotStatus;
  active: boolean;
  icon: ReactNode;
  selected: boolean;
  onSelect: (event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => void;
  onToggleActive: () => void;
  onDelete?: () => void | Promise<void>;
  onRequestDelete?: () => void;
  onDuplicate?: () => void | Promise<void>;
  onEdit?: () => void;
  onRename?: (newName: string) => void;
  onHover?: (id: string | null) => void;
  onContextMenu?: (event: React.MouseEvent<HTMLDivElement>) => void;
  orbitStatus?: OrbitStatusMeta;
  grouped?: boolean;
  showDot?: boolean;
  sensors?: SensorData[];
  disabled?: boolean;
  unusedInReport?: boolean;
  orbitColour?: string | null;
}

const DOT_COLORS: Record<DotStatus, string> = {
  ok: 'var(--accent, #ccf54e)',
  warn: 'rgb(218, 98, 33)',
  info: 'rgb(80, 160, 255)',
  none: 'var(--fg5, rgba(255,255,255,0.2))',
};

const SHOW_ORBIT_COLOR_INDICATOR = true;

type RowActionState = 'idle' | 'duplicating';

interface TreeItemContextType {
  openMenuId: string | null;
  setOpenMenuId: (id: string | null) => void;
}

const TreeItemContext = React.createContext<TreeItemContextType>({
  openMenuId: null,
  setOpenMenuId: () => { },
});

const TreeItem: React.FC<TreeItemProps> = ({
  id, name, dot, active, icon, selected, onSelect, onToggleActive, onDelete, onRequestDelete, onDuplicate, onEdit, onRename, onHover,
  onContextMenu, grouped = false, showDot = true, sensors = [], disabled = false, unusedInReport = false, orbitColour,
}) => {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(name);
  const [actionState, setActionState] = useState<RowActionState>('idle');
  const [menuOpen, setMenuOpen] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const [sensorsOpen, setSensorsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { openMenuId, setOpenMenuId } = React.useContext(TreeItemContext) || { openMenuId: null, setOpenMenuId: () => { } };

  useEffect(() => {
    if (renaming) {
      setDraft(name);
      setTimeout(() => inputRef.current?.select(), 0);
    }
  }, [renaming, name]);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: Event) => {
      const target = e.target as Node;
      // Close menu if click is outside the menu ref and the trigger button
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        setMenuOpen(false);
        setOpenMenuId(null);
      }
    };
    // Use capture phase to catch events from canvas/Cesium viewer
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [menuOpen, setOpenMenuId]);

  useEffect(() => {
    if (openMenuId && openMenuId !== id && menuOpen) {
      setMenuOpen(false);
    }
  }, [openMenuId, id, menuOpen]);

  const commitRename = () => {
    setRenaming(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== name && onRename) onRename(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
    if (e.key === 'Escape') { setRenaming(false); setDraft(name); }
  };

  const handleDuplicate = async () => {
    setMenuOpen(false);
    setActionState('duplicating');
    try {
      await onDuplicate?.();
      setActionState('idle');
    } catch {
      setActionState('idle');
    }
  };

  const handleEdit = () => {
    setMenuOpen(false);
    onEdit?.();
  };

  const isDuplicating = actionState === 'duplicating';
  const isBusy = isDuplicating;
  const sensorCount = sensors.length;
  const sensorTitle = sensorCount > 0
    ? sensors.map((sensor, index) => {
      const label = sensor.name?.trim() || sensor.sensorId?.trim() || `Sensor ${index + 1}`;
      return `${label} (${sensor.geometry})`;
    }).join('\n')
    : '';

  return (
    <div>
      <div
        className={[
          styles.treeItem,
          grouped ? styles.groupedTreeItem : '',
          (selected || menuOpen) ? styles.selected : '',
          !active ? styles.inactive : '',
          isBusy ? styles.treeItemDeleting : '',
          unusedInReport ? styles.unusedInReport : '',
        ].filter(Boolean).join(' ')}
        onClick={renaming || isBusy || unusedInReport ? undefined : onSelect}
        role="button"
        tabIndex={unusedInReport ? -1 : 0}
        onKeyDown={e => {
          if (renaming || isBusy || unusedInReport || e.key !== 'Enter') return;
          onSelect(e);
        }}
        onMouseEnter={() => onHover?.(id)}
        onMouseLeave={() => onHover?.(null)}
        onContextMenu={onContextMenu}
      >
        {isBusy ? (
          <>
            <span className={styles.rowSpinner} />
            <span className={styles.itemName} style={{ opacity: 0.4 }}>
              {isDuplicating ? `Duplicating ${name}` : name}
            </span>
          </>
        ) : (
          <>
            {!grouped && showDot && (
              <span
                className={styles.dot}
                style={{
                  background: active ? DOT_COLORS[dot] : 'var(--fg5, rgba(255,255,255,0.2))',
                  boxShadow: active && dot !== 'none' ? `0 0 5px ${DOT_COLORS[dot]}` : 'none',
                }}
              />
            )}
            {sensorCount > 0 && !renaming && (
              <button
                className={`${styles.sensorToggle} ${sensorsOpen ? styles.sensorToggleOpen : ''}`}
                type="button"
                title={sensorsOpen ? 'Hide sensors' : `Show ${sensorCount} sensor${sensorCount > 1 ? 's' : ''}`}
                aria-label={sensorsOpen ? 'Hide sensors' : `Show ${sensorCount} sensor${sensorCount > 1 ? 's' : ''}`}
                aria-expanded={sensorsOpen}
                onClick={(event) => {
                  event.stopPropagation();
                  setSensorsOpen(open => !open);
                }}
              >
                <SensorCaretIcon open={sensorsOpen} />
              </button>
            )}
            <span className={styles.itemIcon}>{icon}</span>
            {SHOW_ORBIT_COLOR_INDICATOR && orbitColour && (
              <span
                className={styles.colorIndicator}
                style={{
                  backgroundColor: orbitColour.includes('#') ? orbitColour : `rgba(${orbitColour.replace('[', '').replace(']', '')})`,
                }}
              />
            )}
            {renaming ? (
              <input
                ref={inputRef}
                className={styles.renameInput}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onBlur={commitRename}
                onKeyDown={handleKeyDown}
                onClick={e => e.stopPropagation()}
              />
            ) : (
              <span className={styles.itemNameWrapper}>
                <CustomToolTip title={name} placement="right" className={styles.itemName}>
                  {name}
                </CustomToolTip>
              </span>
            )}
            <div className={`${styles.actions} ${menuOpen ? styles.visible : ''}`} onClick={e => e.stopPropagation()}>
                <button
                  className={`${styles.actionBtn} ${!active ? styles.eyeOff : ''}`}
                  type="button"
                  title={active ? 'Hide' : 'Show'}
                  onClick={onToggleActive}
                >
                  {active ? <EyeOnIcon /> : <EyeOffIcon />}
                </button>
                {!disabled && !grouped && (
                  <div className={styles.moreWrap}>
                    <button
                      className={styles.actionBtn}
                      type="button"
                      title="More"
                      ref={triggerRef}
                      onClick={(e) => {
                        const newState = !menuOpen;
                        setMenuOpen(newState);
                        setOpenMenuId(newState ? id : null);
                        if (newState) {
                          setTriggerRect(e.currentTarget.getBoundingClientRect());
                        }
                      }}
                    >
                      <MoreIcon />
                    </button>
                    {menuOpen && triggerRect && ReactDOM.createPortal(
                      <div
                        className={styles.rowMenu}
                        ref={menuRef}
                        style={{
                          position: 'fixed',
                          top: triggerRect.top,
                          left: triggerRect.right + 8,
                          right: 'auto',
                          zIndex: 99999,
                        }}
                      >
                         {onRename && (
                          <button
                            className={styles.rowMenuItem}
                            type="button"
                            onClick={() => {
                              setMenuOpen(false);
                              setOpenMenuId(null);
                              setRenaming(true);
                            }}
                          >
                            <RenameIcon />
                            <span>Rename</span>
                          </button>
                        )}

                        {onEdit && (
                          <button className={styles.rowMenuItem} type="button" onClick={handleEdit} disabled={!active} title={!active ? "Satellite must be visible to edit" : undefined}>
                            <EditIcon />
                            <span>Edit</span>
                          </button>
                        )}

                        {onDuplicate && (
                          <button className={styles.rowMenuItem} type="button" onClick={handleDuplicate} disabled={!active} title={!active ? "Satellite must be visible to duplicate" : undefined}>
                            <DuplicateIcon />
                            <span>Duplicate</span>
                          </button>
                        )}

                        {onDelete && (
                          <button
                            className={`${styles.rowMenuItem} ${styles.rowMenuDanger}`}
                            type="button"
                            onClick={() => { setMenuOpen(false); setOpenMenuId(null); onRequestDelete?.(); }}
                            disabled={!active}
                            title={!active ? "Satellite must be visible to delete" : undefined}
                          >
                            <TrashIcon />
                            <span>Delete</span>
                          </button>
                        )}

                      </div>,
                      document.body
                    )}
                  </div>
                )}
              </div>
          </>
        )}
      </div>
      {sensorCount > 0 && sensorsOpen && !isBusy && (
        <div
          className={`${styles.sensorList} ${grouped ? styles.groupedSensorList : ''} ${!active ? styles.sensorListInactive : ''}`}
          title={sensorTitle}
          aria-label={`${sensorCount} sensor${sensorCount > 1 ? 's' : ''}`}
        >
          {sensors.map((sensor, index) => {
            const label = sensor.name?.trim() || sensor.sensorId?.trim() || `Sensor ${index + 1}`;
            return (
              <div className={styles.sensorRow} key={sensor.sensorId || `${label}-${index}`}>
                <span className={styles.sensorGlyph} />
                <span className={styles.sensorName}>{label}</span>
                <span className={styles.sensorGeometry}>{sensor.geometry}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TreeItem;
export { TreeItemContext };
